import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import Tooltip from '@jimmyboys/liita-ui/dist/components/Tooltip'

import styles from './ClinicalProcessHeader.module.sass'
import useSession from 'hooks/useSession'
import { CoordinatorType } from 'utils/enums'
import { API_URL, fetchOptions, parseJSONContent } from 'store/fetchUtils'
import { useParams } from 'react-router-dom/cjs/react-router-dom.min'
import { useReload } from 'store'

const PullButton = (props) => {
	const { showDialog, pushNotification, expireSession } = useGlobal()
	const { clinicalProcessId } = useParams()
	const { user: sessionUser } = useSession()
	const reload = useReload()

	const { labels, type } = props

	return (
		<Tooltip text={labels.followCoordinatorTooltip} displayDelay={0}>
			<small
				className={styles['small-button-link']}
				onClick={() => {
					showDialog(
						'ConfirmDialog',
						labels.followProcessConfirmationTitle,
						{
							label: labels.followProcessConfirmationMessage,
						},
						false,
						undefined,
						'center',
						'transfer-coordination'
					)
						.then(() => {
							const url = `${API_URL}/api/clinical_processes/${clinicalProcessId}/transfer_coordinator`

							const body = {
								coordinatorId: sessionUser.username,
								coordinationType:
									type === CoordinatorType.ClinicalCoordinator
										? 'ClinicalCoordinator'
										: 'AdministrativeCoordinator',
								observation: labels.atHisOwnRequest,
							}

							fetch(
								url,
								fetchOptions({
									method: 'PUT',
									body,
								})
							)
								.then((r) => parseJSONContent(r))
								.then(() => {
									reload([
										`/clinical_processes/${clinicalProcessId}`,
										`/clinical_processes/${clinicalProcessId}/coordinator_allocation_history`,
									])
									pushNotification(
										undefined,
										'success',
										labels.followProcessSuccess
									)
								})
								.catch((err) => {
									pushNotification(
										undefined,
										'error',
										labels.followProcessError
									)
									if (
										err.status === '401' ||
										err.status === 401
									) {
										expireSession()
									}
								})
						})
						.catch(() => null)
				}}
			>
				{`(${labels.followProcess})`}
			</small>
		</Tooltip>
	)
}

const ReleaseButton = (props) => {
	const { showDialog, pushNotification, expireSession } = useGlobal()
	const { clinicalProcessId } = useParams()
	const { user: sessionUser } = useSession()
	const reload = useReload()

	const { labels, type } = props

	return (
		<Tooltip text={labels.unfollowCoordinatorTooltip} displayDelay={0}>
			<small
				className={styles['small-button-link']}
				onClick={() => {
					showDialog(
						'ConfirmDialog',
						labels.unfollowProcessConfirmationTitle,
						{
							label: labels.unfollowProcessConfirmationMessage,
						},
						false,
						undefined,
						'center',
						'transfer-coordination'
					)
						.then(() => {
							const url = `${API_URL}/api/clinical_processes/${clinicalProcessId}/unlock_coordinator`

							const body = {
								coordinatorId: sessionUser.username,
								coordinationType:
									type === CoordinatorType.ClinicalCoordinator
										? 'ClinicalCoordinator'
										: 'AdministrativeCoordinator',
								observation: labels.atHisOwnRequest,
							}

							fetch(
								url,
								fetchOptions({
									method: 'PUT',
									body,
								})
							)
								.then((r) => parseJSONContent(r))
								.then(() => {
									reload([
										`/clinical_processes/${clinicalProcessId}`,
										`/clinical_processes/${clinicalProcessId}/check_coordinator_lock`,
									])
									pushNotification(
										undefined,
										'success',
										labels.unfollowProcessSuccess
									)
								})
								.catch((err) => {
									pushNotification(
										undefined,
										'error',
										labels.unfollowProcessError
									)
									if (
										err.status === '401' ||
										err.status === 401
									) {
										expireSession()
									}
								})
						})
						.catch(() => null)
				}}
			>
				{`(${labels.unfollowProcess})`}
			</small>
		</Tooltip>
	)
}

const ManageCoordinatorButton = (props) => {
	const { i18n } = useGlobal()
	const { user: sessionUser } = useSession()

	const { type, currentCoordinator, lockedCoordinators } = props

	const transferLabels = {
		followProcess: i18n`Follow process`,
		followProcessSuccess: i18n`Process followed successfully!`,
		followProcessError: i18n`An error occur. Please try again later.`,
		followCoordinatorTooltip: i18n`Transfer coordinator to me`,
		followProcessConfirmationTitle: i18n`Follow Process`,
		followProcessConfirmationMessage: i18n`Are you sure you want to follow the process?`,
		atHisOwnRequest: i18n`At his own request.`,
	}

	const releaseLabels = {
		unfollowProcess: i18n`Unfollow process`,
		unfollowProcessSuccess: i18n`Process unfollowed successfully!`,
		unfollowProcessError: i18n`An error occur. Please try again later.`,
		unfollowCoordinatorTooltip: i18n`Release coordinator`,
		unfollowProcessConfirmationTitle: i18n`Unfollow Process`,
		unfollowProcessConfirmationMessage: i18n`Are you sure you want to unfollow the process?`,
		atHisOwnRequest: i18n`At his own request.`,
	}

	const userRoles = sessionUser?.roles || []

	const isManager = userRoles.includes('manager')
	const isDoctor = userRoles.includes('doctor')

	const coordinatorType = isDoctor
		? CoordinatorType.ClinicalCoordinator
		: CoordinatorType.AdministrativeManager

	const showPullButtonForDoctor =
		type === CoordinatorType.ClinicalCoordinator &&
		isDoctor &&
		currentCoordinator?.id.toLowerCase() !==
			sessionUser.username.toLowerCase()

	const showPullButtonForManager =
		type === CoordinatorType.AdministrativeManager &&
		isManager &&
		currentCoordinator?.id.toLowerCase() !==
			sessionUser.username.toLowerCase()

	const showReleaseButtonForDoctor =
		type === CoordinatorType.ClinicalCoordinator &&
		isDoctor &&
		currentCoordinator?.id.toLowerCase() ===
			sessionUser.username.toLowerCase() &&
		lockedCoordinators?.[coordinatorType]
	const showReleaseButtonForManager =
		type === CoordinatorType.AdministrativeManager &&
		isManager &&
		currentCoordinator?.id.toLowerCase() ===
			sessionUser.username.toLowerCase() &&
		lockedCoordinators?.[coordinatorType]

	return showPullButtonForDoctor || showPullButtonForManager ? (
		<PullButton labels={transferLabels} type={type} />
	) : showReleaseButtonForDoctor || showReleaseButtonForManager ? (
		<ReleaseButton labels={releaseLabels} type={type} />
	) : null
}

export default ManageCoordinatorButton
